import React, { useEffect, useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';

interface EmailDomainValidatorProps {
  allowedDomain: string;
  children: React.ReactNode;
}

const EmailDomainValidator: React.FC<EmailDomainValidatorProps> = ({ 
  allowedDomain, 
  children 
}) => {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoaded && user) {
      // Get primary email address
      const primaryEmail = user.primaryEmailAddress?.emailAddress;
      
      if (primaryEmail) {
        // Extract domain from email
        const emailDomain = primaryEmail.split('@')[1];
        
        // Check if domain matches allowed domain
        if (emailDomain !== allowedDomain) {
          setIsValidating(true);
          setError(`Only @${allowedDomain} email addresses are allowed.`);
          
          // Sign out the user
          setTimeout(() => {
            signOut();
          }, 3000); // Give them 3 seconds to read the message
        } else {
          // Valid domain
          setError(null);
          setIsValidating(false);
        }
      }
    }
  }, [user, isLoaded, allowedDomain, signOut]);

  // Show error message if invalid domain
  if (error && isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            {/* Error Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            {/* Error Message */}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h1>
            <p className="text-gray-600 mb-6">{error}</p>

            {/* Additional Info */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                Please sign up with your <strong>@{allowedDomain}</strong> email address to access this application.
              </p>
            </div>

            {/* Signing out message */}
            <p className="text-sm text-gray-500">
              Signing you out...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render children if validation passes or still loading
  return <>{children}</>;
};

export default EmailDomainValidator;

